import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TemplateField } from '../../../core/models/template-field';
import { LookupsApiService } from '../../../core/api-services/lookups-api.services';
import { TemplateApiService } from '../../../core/api-services/template-api.service';
import { PaginationComponent } from '../../../shared/pagination/pagination';
import { Country } from '../../../core/models/country';
import { FormFieldCategory } from '../../../core/models/form-field-category';
import { FormFieldType } from '../../../core/models/form-field-type';
import { ApplicationStatus } from '../../../core/models/application-status';
import { GradePointAverage } from '../../../core/models/grade-point-average';
import { Requirement } from '../../../core/models/requirement';
import { DocumentFormat } from '../../../core/models/document-format';
import { DocumentTypeSelection } from '../../../core/interfaces/document-type-selection';
import { DocumentType } from '../../../core/models/document-type';
import { PlatformService } from '../../../core/services/platform.service';
import { DocumentTypeFormat } from '../../../core/models/document-type-format';
import { CreateScholarshipTemplate, ScholarshipTemplate, UpdateScholarshipTemplate } from '../../../core/models/scholarship-template';
import { CreateTemplateFieldOption } from '../../../core/models/template-field-option';
import { ScholarshipTemplateField } from '../../../core/models/scholarship-template-field';
import { ConfirmationService } from '../../../core/services/confirmation';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import { AdminCreationDraftApiService } from '../../../core/api-services/admin-creation-draft-api.service';
import { AdminCreationDraft } from '../../../core/models/admin-creation-draft';

@Component({
  selector: 'app-admin-templates',
  imports: [CommonModule, FormsModule, DatePipe, PaginationComponent],
  templateUrl: './templates.html',
  styleUrl: './templates.scss',
})
export class AdminTemplatesComponent implements OnInit, OnDestroy {
  private readonly templateApi = inject(TemplateApiService);
  private readonly platform = inject(PlatformService);
  private readonly lookupsService = inject(LookupsApiService);
  private confirmation = inject(ConfirmationService);
  private readonly draftApi = inject(AdminCreationDraftApiService);
  private autosaveSubscription?: Subscription;
  private lastSavedDraftJson = '';
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  currentDate = new Date();
  filteredRequiredDocuments: DocumentTypeFormat[] = [];
  saveMessage = '';
  errorMessage = '';
  searchQuery = '';
  filterCountry = '';
  filterStatus = '';
  showCreateTemplateModal = false;
  showDeleteTemplateConfirm = false;
  templateToDeleteId: number = 0;
  deleteErrorMessage = '';
  currentPage = 1;
  readonly pageSize = 5;
  today: string = '';

  templateName = '';
  targetCountryId: number | null = null;
  gradePointAverageId: number | null = null;
  templateDescription = '';

  editingTemplateId: number = 0;

  fields: TemplateField[] = [];
  fieldOptionsText: string[] = [];
  savedTemplates: ScholarshipTemplate[] = [];
  documentTypeSelections: DocumentTypeSelection[] = [];
  documentFormats: DocumentFormat[] = [];

  countries: Country[] = [];
  fieldCategories: FormFieldCategory[] = [];
  fieldTypes: FormFieldType[] = [];
  statusOptions: ApplicationStatus[] = [];
  gradePointAverages: GradePointAverage[] = [];
  scholarshipRequirements: Requirement[] = [];
  loading: boolean = false;
  availableDraft?: AdminCreationDraft<TemplateDraftPayload>;
  isSavingDraft = false;
  draftMessage = '';

  get fieldTypeOptions(): string[] {
    return [...this.fieldTypes]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => t.name);
  }

  get categoryOptions(): string[] {
    return [...this.fieldCategories]
      .sort((a, b) => a.orderNumber - b.orderNumber)
      .map((c) => c.name);
  }

  formatGradePointAverageRange(minimumGpa: number, maximumGpa: number): string {
    if (!minimumGpa && !maximumGpa) {
      return 'N/A';
    }

    if (minimumGpa === maximumGpa) {
      return `${minimumGpa}%`;
    }

    return `${minimumGpa}% - ${maximumGpa}%`;
  }

  ngOnInit(): void {
    const now = new Date();
    this.today = now.toISOString().split('T')[0]; // YYYY-MM-DD

    this.lookupsService.getCountries().subscribe((data: Country[]) => {
      this.countries = data;
    });

    this.lookupsService.getFormFieldCategories().subscribe((data: FormFieldCategory[]) => {
      this.fieldCategories = data;
    });

    this.lookupsService.getFormFieldTypes().subscribe((data: FormFieldType[]) => {
      this.fieldTypes = data;
    });

    this.lookupsService.getGradePointAverages().subscribe((data: GradePointAverage[]) => {
      this.gradePointAverages = data;
    });

    this.lookupsService.getScholarshipRequirements().subscribe((data: Requirement[]) => {
      this.scholarshipRequirements = data;
    });

    this.lookupsService.getDocumentTypes().subscribe();
    this.lookupsService.getDocumentFormats().subscribe();

    this.loadSavedTemplates();
    this.loadApplicationStatuses();
    this.loadAvailableDraft();
    this.autosaveSubscription = interval(2000).subscribe(() => this.saveDraftIfChanged());
  }

  ngOnDestroy(): void {
    this.autosaveSubscription?.unsubscribe();
  }

  loadApplicationStatuses() {
    if (!this.platform.isBrowser)
      return;

    this.lookupsService.getApplicationStatuses().subscribe(
      (data: ApplicationStatus[]) => {
        this.statusOptions = data;
        this.loading = false;
      },
      (error: any) => {
        this.loading = false;
        console.error(error);
      }
    );
  }

  getDistinctDocumentTypeNames(template: ScholarshipTemplate): string[] {
    return [
      ...new Set(
        template.documentTypeFormats.map(
          (d: DocumentTypeFormat) => d.documentType.name
        )
      )
    ];
  }

  get filteredTemplates(): ScholarshipTemplate[] {
    let results = this.savedTemplates;

    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      results = results.filter(
        t =>
          t.name.toLowerCase().includes(query) ||
          t.country.name.toLowerCase().includes(query)
      );
    }

    if (this.filterCountry) {
      results = results.filter(t => t.country.name === this.filterCountry);
    }

    if (this.filterStatus) {
      results = results.filter(t => this.getTemplateStatus(t) === this.filterStatus);
    }

    return results;
  }

  get paginatedTemplates(): ScholarshipTemplate[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredTemplates.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredTemplates.length / this.pageSize));
  }

  get showingFrom(): number {
    return this.filteredTemplates.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredTemplates.length);
  }

  getTemplateStatus(template: ScholarshipTemplate): string {
    return template.fields?.length > 0 ? 'COMPLETED' : 'DRAFT';
  }

  getCountryFlagUrl(countryName: string): string {
    const country = this.countries.find((c) => c.name === countryName);
    if (country?.flagUrl) return country.flagUrl;
    if (country?.code) return `https://flagcdn.com/16x12/${country.code.toLowerCase()}.png`;
    return '';
  }

  getCountryIsoCode(countryName: string): string {
    return this.countries.find((c) => c.name === countryName)?.code ?? '';
  }

  openCreateTemplateModal(): void {
    this.saveMessage = '';
    this.draftMessage = '';
    this.resetTemplateForm();
    this.showCreateTemplateModal = true;
  }

  resumeDraft(): void {
    const draft = this.availableDraft?.payload;
    if (!draft || !this.fieldTypes.length || !this.fieldCategories.length) {
      this.errorMessage = 'Draft data is still loading. Please try again in a moment.';
      return;
    }
    this.resetTemplateForm();
    this.templateName = draft.templateName ?? '';
    this.targetCountryId = draft.targetCountryId ?? null;
    this.gradePointAverageId = draft.gradePointAverageId ?? null;
    this.templateDescription = draft.templateDescription ?? '';
    this.fields = (draft.fields ?? []).map(field => ({
      ...field,
      fieldType: this.fieldTypes.find(type => type.id === field.fieldTypeId) ?? this.fieldTypes[0],
      fieldCategory: this.fieldCategories.find(category => category.id === field.fieldCategoryId) ?? this.fieldCategories[0],
      options: [],
    }));
    this.fieldOptionsText = draft.fieldOptionsText ?? [];
    this.documentTypeSelections = draft.documentTypeSelections ?? [];
    this.documentFormats = draft.documentFormats ?? [];
    this.lastSavedDraftJson = JSON.stringify(this.draftPayload());
    this.showCreateTemplateModal = true;
    this.draftMessage = 'Draft reopened. Changes are saved automatically.';
  }

  closeCreateTemplateModal(saveDraft = true): void {
    if (saveDraft) this.saveDraftIfChanged();
    this.showCreateTemplateModal = false;
    this.errorMessage = '';
    this.draftMessage = '';
    //this.resetTemplateForm();
  }

  addTemplateField(): void {
    const defaultCategory = this.categoryOptions[0] ?? 'Personal Information';
    const defaultType = this.fieldTypeOptions[0] ?? 'Text';
    const defaultFieldType = this.fieldTypes.find((t) => t.name === defaultType) ?? null!;
    const defaultFieldCategory = this.fieldCategories.find((c) => c.name === defaultCategory) ?? null!;

    this.fields.push({
      label: '',
      fieldType: defaultFieldType,
      isRequired: false,
      fieldCategory: defaultFieldCategory,
      options: [],
      id: 0,
      fieldKey: '',
      displayOrder: 0
    });
    this.fieldOptionsText.push('');
  }

  removeTemplateField(index: number): void {
    this.fields = this.fields.filter((_, i) => i !== index);
    this.fieldOptionsText.splice(index, 1);
  }

  insertTemplateField(afterIndex: number): void {
    if (!this.fieldTypes.length || !this.fieldCategories.length) return;

    const defaultCategory = this.categoryOptions[0] ?? 'Personal Information';
    const defaultType = this.fieldTypeOptions[0] ?? 'Text';
    const defaultFieldType = this.fieldTypes.find((t) => t.name === defaultType) ?? null!;
    const defaultFieldCategory = this.fieldCategories.find((c) => c.name === defaultCategory) ?? null!;

    const newField = {
      label: '',
      fieldType: defaultFieldType,
      isRequired: false,
      fieldCategory: defaultFieldCategory,
      options: [],
      id: 0,
      fieldKey: '',
      displayOrder: 0
    };

    this.fields.splice(afterIndex + 1, 0, newField);
    this.fieldOptionsText.splice(afterIndex + 1, 0, '');
  }

  requiresOptions(fieldType: string): boolean {
    return ['dropdown', 'yes-no', 'radio', 'select', 'checkbox', 'multi-select'].includes(
      fieldType.toLowerCase()
    );
  }

  onFieldDefinitionChanged(index: number): void {
    const field = this.fields[index];
    if (!field) return;
    //this.ensureDisabilityFollowUpField(index, field);
  }

  loadDhetRequiredFieldSet(): void {
    this.lookupsService.getTemplateFields().subscribe((data: TemplateField[]) => {
      this.fields = data;
      this.fieldOptionsText = data.map(f =>
        f.options?.map(o => o.displayText).join(', ') ?? ''
      );

      this.loadDocumentTypesAndFormats();
      this.saveMessage = 'DHET required field set loaded. Review and save template.';
    });
  }

  loadRequiredDocuments(): void {
    this.loadDocumentTypesAndFormats();
  }

  loadDocumentTypesAndFormats(): void {
    this.lookupsService.getDocumentTypes().subscribe((data: DocumentType[]) => {
      this.documentTypeSelections = data.map((dt) => ({
        documentType: dt,
        isRequired: false,
        allowedFormatIds: []
      }));
    });

    this.lookupsService.getDocumentFormats().subscribe((data: DocumentFormat[]) => {
      this.documentFormats = data;
    });
  }

  openTemplateForEdit(templateId: number): void {
    const template = this.savedTemplates.find((t) => t.id === templateId);
    if (!template) return;

    this.editingTemplateId = template.id;
    this.templateName = template.name;
    this.targetCountryId = template.countryId;
    this.gradePointAverageId = template.gradePointAverage?.id ?? null;
    this.templateDescription = template.description;

    this.fields = template.fields.map(f => f.templateField);
    this.fieldOptionsText = template.fields.map(f =>
      f.templateField.options?.map(o => o.displayText).join(', ') ?? ''
    );

    this.lookupsService.getDocumentTypes().subscribe((data) => {
      this.documentTypeSelections = data.map((dt) => {
        const existing = template.documentTypeFormats.filter(
          dtf => dtf.documentType.id === dt.id
        );
        return {
          documentType: dt,
          isRequired: existing.length > 0,
          allowedFormatIds: existing.map(dtf => dtf.documentFormat.id).filter(id => id > 0)
        };
      });
    });

    this.lookupsService.getDocumentFormats().subscribe((data) => {
      this.documentFormats = data;
    });

    this.showCreateTemplateModal = true;
  }

  async requestDeleteTemplate(templateId: number): Promise<void> {
    let template = this.savedTemplates.filter((t: ScholarshipTemplate) => t.id === templateId)[0];

    const confirmed = await this.confirmation.open({
      title: 'Delete Template?',
      message: 'You are about to delete template : [' + (template?.name) + ']. This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    });

    debugger

    if (!confirmed) {
      this.templateToDeleteId = 0;
      return;
    }

    this.templateToDeleteId = templateId;

    this.templateApi.deleteScholarshipTemplate(templateId).subscribe(result => {

    });
  }

  cancelDeleteTemplate(): void {
    this.templateToDeleteId = 0;
    this.deleteErrorMessage = '';
    this.showDeleteTemplateConfirm = false;
  }

  confirmDeleteTemplate(): void {
    if (!this.templateToDeleteId) return;
    this.templateApi.deleteScholarshipTemplate(this.templateToDeleteId).subscribe(
      () => {
        this.saveMessage = 'Template deleted successfully.';
        this.cancelDeleteTemplate();
        this.templateApi.invalidateCache();
        this.loadSavedTemplates();
        setTimeout(() => { this.saveMessage = ''; }, 3000);
      },
      (error) => {
        this.deleteErrorMessage = 'Failed to delete template. Please try again.';
        console.error(error);
      }
    );
  }

  resetTemplateForm(): void {
    this.templateName = null!;
    this.gradePointAverageId = null!;
    this.targetCountryId = null!;
    this.templateDescription = null!;
    this.fields = [];
    this.fieldOptionsText = [];
    this.documentFormats = [];
    this.documentTypeSelections = [];
    this.editingTemplateId = null!;
  }

  createTemplate(): void {
    this.errorMessage = '';

    if (!this.isTemplateFormValid()) {
      return;
    }

    debugger

    const paired = this.fields
      .map((f, i) => ({ field: f, optionsText: this.fieldOptionsText[i] ?? '' }))
      .filter(({ field }) => field.label?.trim().length > 0);
    this.fields = this.normalizeTemplateFields(paired.map(p => p.field));
    this.fieldOptionsText = paired.map(p => p.optionsText);

    // We should take a different route if we are updating or creating
    if (this.editingTemplateId > 0) {
      const template: UpdateScholarshipTemplate = {
        id: this.editingTemplateId,
        name: this.templateName,
        countryId: this.targetCountryId!,
        gradePointAverageId: this.gradePointAverageId,
        description: this.templateDescription,
        documentTypeSelections: this.documentTypeSelections.filter(t => t.isRequired).map(dts => ({
          id: dts.documentType.id,
          documentTypeId: dts.documentType.id,
          isRequired: dts.isRequired,
          allowedFormatIds: dts.allowedFormatIds
        })),
        fields: this.fields.map((f, i) => ({
          id: f.id,
          fieldKey: f.fieldKey,
          label: f.label,
          isRequired: f.isRequired,
          displayOrder: f.displayOrder,
          fieldTypeId: f.fieldType.id,
          fieldCategoryId: f.fieldCategory.id,
          templateFieldOptions: this.requiresOptions(f.fieldType.name) ? this.parseOptions(this.fieldOptionsText[i] ?? '', f.id) : [],
          applicationTemplateId: 0,
          templateFieldId: f.id
        }))
      };

      // Call the API to save the template
      this.templateApi.updateScholarshipTemplate(template).subscribe(
        (response) => {
          this.saveMessage = `${template.name} updated successfully.`;
          this.resetTemplateForm();
          this.closeCreateTemplateModal(false);
          this.templateApi.invalidateCache();
          this.loadSavedTemplates();
          this.deleteDraft();
        },
        (error) => {
          this.errorMessage = 'Failed to update template. Please try again.';
          console.error(error);
        }
      );
    }
    else {
      const template: CreateScholarshipTemplate = {
        name: this.templateName,
        countryId: this.targetCountryId!,
        gradePointAverageId: this.gradePointAverageId,
        description: this.templateDescription,
        documentTypeSelections: this.documentTypeSelections.filter(t => t.isRequired).map(dts => ({
          documentTypeId: dts.documentType.id,
          isRequired: dts.isRequired,
          allowedFormatIds: dts.allowedFormatIds
        })),
        fields: this.fields.map((f, i) => ({
          id: f.id || '',
          fieldKey: f.fieldKey || this.generateFieldKey(f.label),
          label: f.label,
          isRequired: f.isRequired,
          displayOrder: f.displayOrder,
          fieldTypeId: f.fieldType.id,
          fieldCategoryId: f.fieldCategory.id,
          templateFieldOptions: this.requiresOptions(f.fieldType.name) ? this.parseOptions(this.fieldOptionsText[i] ?? '', f.id) : [],
          applicationTemplateId: 0,
          templateFieldId: f.id || 0
        }))
      };

      // Call the API to save the template
      this.templateApi.saveScholarshipTemplate(template).subscribe(
        (response) => {
          this.saveMessage = `${template.name} created successfully.`;
          this.resetTemplateForm();
          this.closeCreateTemplateModal(false);
          this.templateApi.invalidateCache();
          this.loadSavedTemplates();
          this.deleteDraft();
        },
        (error) => {
          this.errorMessage = 'Failed to save template. Please try again.';
          console.error(error);
        }
      );
    }



    // const existingIndex = this.savedTemplates.findIndex((t: any) => t.id === payload.id);
    // const previousSnapshot = existingIndex > -1 ? { ...this.savedTemplates[existingIndex] } : undefined;

    // if (existingIndex > -1) {
    //   this.savedTemplates[existingIndex] = payload;
    //   this.saveMessage = `${payload.name} updated successfully.`;
    // } else {
    //   this.savedTemplates.unshift(payload);
    //   this.saveMessage = `${payload.name} created successfully.`;
    // }

    // this.persistSavedTemplates();

    // this.templateApi.saveTemplateDraft(payload);
    // this.templateApi.appendAuditLog({
    //   action: existingIndex > -1 ? 'TEMPLATE_UPDATED' : 'TEMPLATE_CREATED',
    //   timestamp: nowIso,
    //   targetCountry: this.targetCountry,
    //   templateName: this.templateName,
    //   fieldCount: this.templateFields.length,
    //   snapshot: previousSnapshot,
    // });

    // this.showCreateTemplateModal = false;
    // this.currentPage = 1;
    // setTimeout(() => { this.saveMessage = ''; }, 4000);

  }

  onFiltersChanged(): void {
    this.currentPage = 1;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  isTemplateFormValid(): boolean {
    if (!this.templateName.trim() || this.targetCountryId === null || this.gradePointAverageId === null) {
      this.errorMessage = 'Template Name, Target Country, and Minimum Average (%) are required.';
      return false;
    }

    if (!this.templateDescription.trim()) {
      this.errorMessage = 'Template Description is required.';
      return false;
    }

    if (!this.fields || this.fields.length === 0) {
      this.errorMessage = 'At least one application field is required.';
      return false;
    }

    if (!this.documentTypeSelections || this.documentTypeSelections.length === 0) {
      this.errorMessage = 'At least one required document is required.';
      return false;
    }

    const invalid = this.documentTypeSelections
      .filter(x => x.isRequired && (!x.allowedFormatIds || x.allowedFormatIds.length === 0))
      .map(x =>
        `'${x.documentType.name}' is marked as required but has no allowed formats selected.`
      );
    if (invalid.length > 0) {
      this.errorMessage = invalid.join('\n');
      return false;
    }

    return true;
  }

  private loadSavedTemplates(): void {
    this.templateApi.getAllScholarshipTemplates().subscribe(dt => {
      this.savedTemplates = dt;
    });
  }

  saveDraft(): void {
    this.saveDraftIfChanged(true);
  }

  private saveDraftIfChanged(force = false): void {
    if (!this.showCreateTemplateModal || this.isSavingDraft) return;
    const payload = this.draftPayload();
    if (!force && !this.hasTemplateDraftContent(payload)) return;
    const payloadJson = JSON.stringify(payload);
    if (!force && payloadJson === this.lastSavedDraftJson) return;

    this.isSavingDraft = true;
    this.draftApi.save('scholarship-template', this.templateName.trim() || 'Untitled template', payload).subscribe({
      next: () => {
        this.lastSavedDraftJson = payloadJson;
        this.availableDraft = { displayName: this.templateName.trim() || 'Untitled template', payload, modifiedDate: new Date().toISOString() };
        this.draftMessage = 'Draft saved';
        this.isSavingDraft = false;
      },
      error: () => { this.isSavingDraft = false; },
    });
  }

  private loadAvailableDraft(): void {
    this.draftApi.get<TemplateDraftPayload>('scholarship-template').subscribe({ next: draft => this.availableDraft = draft, error: () => undefined });
  }

  private deleteDraft(): void {
    this.draftApi.delete('scholarship-template').subscribe({ next: () => { this.availableDraft = undefined; this.lastSavedDraftJson = ''; }, error: () => undefined });
  }

  private draftPayload(): TemplateDraftPayload {
    return {
      templateName: this.templateName,
      targetCountryId: this.targetCountryId,
      gradePointAverageId: this.gradePointAverageId,
      templateDescription: this.templateDescription,
      fields: this.fields.map(field => ({ ...field, fieldTypeId: field.fieldType?.id, fieldCategoryId: field.fieldCategory?.id })),
      fieldOptionsText: this.fieldOptionsText,
      documentTypeSelections: this.documentTypeSelections,
      documentFormats: this.documentFormats,
    };
  }

  private hasTemplateDraftContent(payload: TemplateDraftPayload): boolean {
    return !!(payload.templateName || payload.templateDescription || payload.targetCountryId || payload.fields.length || payload.documentTypeSelections.some(item => item.isRequired));
  }

  private persistSavedTemplates(): void {
    //this.templateApi.saveTemplates(this.savedTemplates);
  }

  private normalizeTemplateFields(fields: TemplateField[]): TemplateField[] {
    const normalized = fields
      .map((f) => ({
        ...f,
        label: f.label?.trim() ?? '',
        category: f.fieldCategory?.name?.trim() ?? '',
        optionValues:
          f.options?.map((o) => o.displayText?.trim()).join(',') ?? '',
      }))
      .filter((f) => f.label.length > 0);

    // const hasDisabilityQuestion = normalized.some(
    //   (f) =>
    //     f.category.toLowerCase().includes('disability') &&
    //     (f.fieldType?.name === 'Yes-No' ||
    //       /^do\s+you\s+have\s+a\s+disability/i.test(f.label))
    // );

    // if (hasDisabilityQuestion) {
    //   const hasFollowUp = normalized.some(
    //     (f) =>
    //       f.category.toLowerCase().includes('disability') &&
    //       /disability\s*(details|type|description|specify)/i.test(f.label)
    //   );

    //   if (!hasFollowUp) {
    //     normalized.push({
    //       ...normalized[0],
    //       label: 'Disability details (if yes)',
    //       category: 'Disability',
    //       fieldType: {
    //         ...normalized[0]?.fieldType,
    //         name: 'Textarea',
    //       },
    //       isRequired: false,
    //       optionValues: '',
    //       options: [],
    //     });
    //   }
    // }

    return normalized;
  }

  generateFieldKey(label: string): any {
    return label.trim()
      .toLowerCase().replace(/\s+/g, '_');
  }

  private parseOptions(text: string, fieldId: number): CreateTemplateFieldOption[] {
    if (!text?.trim())
      return [];

    return text.split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map((displayText, i) => ({
        templateFieldId: fieldId || 0,
        displayText,
        displayOrder: i + 1,
        value: i + 1
      }));
  }

  // private ensureDisabilityFollowUpField(index: number, field: TemplateField): void {
  //   const isDisabilityYesNo =
  //     field.fieldCategory.name.toLowerCase().includes('disability') &&
  //     (field.fieldType.name === 'yes-no' || /^do\s+you\s+have\s+a\s+disability/i.test(field.label));

  //   if (!isDisabilityYesNo) return;

  //   const hasFollowUp = this.templateFields.some(
  //     (item, i) =>
  //       i !== index &&
  //       item.fieldCategory.name.toLowerCase().includes('disability') &&
  //       /disability\s*(details|type|description|specify)/i.test(item.label),
  //   );

  //   if (!hasFollowUp) {
  //     this.templateFields.splice(index + 1, 0, {
  //       id: '',
  //       label: 'Disability details (if yes)',
  //       isRequired: false,
  //       fieldType: null!,
  //       fieldCategory: null!,
  //       options: [],
  //       fieldKey: '',
  //       displayOrder: 0
  //     });
  //   }
  // }
}

interface TemplateDraftPayload {
  templateName: string;
  targetCountryId: number | null;
  gradePointAverageId: number | null;
  templateDescription: string;
  fields: (TemplateField & { fieldTypeId?: number; fieldCategoryId?: number })[];
  fieldOptionsText: string[];
  documentTypeSelections: DocumentTypeSelection[];
  documentFormats: DocumentFormat[];
}
